import { z, ZodUnion, ZodLiteral } from 'zod';
import { captureException } from '@sentry/minimal';
import { Analytics } from '@segment/analytics-node';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};

// src/utils/cookie/getCookie.ts
var converterRead = (value) => {
  if (value[0] === '"') {
    value = value.slice(1, -1);
  }
  return value.replace(/(%[\dA-F]{2})+/gi, decodeURIComponent);
};
var getCookie = (name) => {
  if (typeof document === "undefined" || !name) return;
  const cookies = document.cookie ? document.cookie.split("; ") : [];
  const jar = {};
  for (let i = 0; i < cookies.length; i++) {
    const parts = cookies[i].split("=");
    const value = parts.slice(1).join("=");
    try {
      const found = decodeURIComponent(parts[0]);
      jar[found] = converterRead(value);
      if (name === found) break;
    } catch (e) {
      break;
    }
  }
  return jar[name];
};
var getCookie_default = getCookie;

// src/utils/cookie/getCookieConsent.tsx
var getConsent = (type) => getCookie_default(type) === "True";
var getCookieConsent = () => {
  if (!isBrowser_default) return {};
  const cookie_category_preferences = {
    consent_cookie_necessary: true,
    consent_cookie_marketing: getConsent("Marketing"),
    consent_cookie_statistic: getConsent("Statistical"),
    consent_cookie_functional: getConsent("Functional")
  };
  const fullConsent = Object.values(cookie_category_preferences).every((e) => e);
  return {
    consent: { cookie_category_preferences, all_cookies_accepted: fullConsent },
    fullConsent
  };
};
var getCookieConsent_default = getCookieConsent;

// src/utils/cookie/setCookie.ts
var setCookie = (name, value, attributes) => {
  if (typeof document === "undefined") {
    return;
  }
  let stringifiedAttributes = "";
  for (const attributeName in attributes) {
    const attrValue = attributes[attributeName];
    if (!attrValue) continue;
    let attributeValue = "";
    switch (attributeName) {
      case "secure":
      case "httponly":
        break;
      case "expires": {
        const expiresValue = getCookiesExpiresValue(attributes.expires);
        if (expiresValue) {
          attributeValue = expiresValue;
        }
        break;
      }
      default:
        attributeValue = `${attrValue}`;
        break;
    }
    const separationChar = attributeValue.length ? "=" : "";
    stringifiedAttributes += `; ${attributeName}${separationChar}${attributeValue}`;
  }
  return document.cookie = getEncodedName(name) + "=" + getConvertedValue(value) + stringifiedAttributes;
};
var getCookiesExpiresValue = (expires) => {
  if (!expires) return null;
  let value = null;
  switch (typeof expires) {
    case "number":
      value = new Date(Date.now() + expires * 864e5).toUTCString();
      break;
    case "string":
      value = expires;
      break;
    default:
      if (expires instanceof Date) {
        value = expires.toUTCString();
      }
      break;
  }
  return value;
};
var getEncodedName = (cookieName) => encodeURIComponent(cookieName).replace(/%(2[346B]|5E|60|7C)/g, decodeURIComponent).replace(/[()]/g, escape);
var getConvertedValue = (cookieValue) => encodeURIComponent(cookieValue).replace(
  /%(2[346BF]|3[AC-F]|40|5[BDE]|60|7[BCD])/g,
  decodeURIComponent
);
var setCookie_default = setCookie;

// src/utils/getGaClientId.ts
var getGaClientId = () => {
  var _a;
  if (!isBrowser_default) return;
  try {
    return window.ga.getAll()[0].get("clientId");
  } catch (e) {
    const gaCookie = (_a = getCookie_default("_ga")) == null ? void 0 : _a.split(".");
    return gaCookie && `${gaCookie[2]}.${gaCookie[3]}`;
  }
};
var getGaClientId_default = getGaClientId;

// src/utils/getUserSegmentId.ts
var getIdFromBrowser = (name) => {
  var _a;
  return ((_a = localStorage.getItem(name)) == null ? void 0 : _a.replace(/['"]+/g, "")) || getCookie_default(name);
};
var normalize = (val) => val === "null" || !val ? void 0 : val;
var getUserSegmentId = () => {
  var _a, _b;
  const analyticsUser = isBrowser_default ? (_b = (_a = window == null ? void 0 : window.analytics) == null ? void 0 : _a.user) == null ? void 0 : _b.call(_a) : null;
  return {
    dfdsUserId: normalize(analyticsUser == null ? void 0 : analyticsUser.id()) || normalize(getIdFromBrowser("ajs_user_id")),
    anonymousUserId: normalize(analyticsUser == null ? void 0 : analyticsUser.anonymousId()) || normalize(getIdFromBrowser("ajs_anonymous_id"))
  };
};
var getUserSegmentId_default = getUserSegmentId;

// src/utils/isBrowser.ts
var isBrowser = typeof window !== "undefined";
var isBrowser_default = isBrowser;

// src/utils/logErrorFormatter.ts
function logTrackError(error, eventPayload) {
  console.error(
    `%cTracking dispatcher errors:

%c${error}

%cEvent payload:`,
    "color: #5b70d7",
    "color: #dd6262",
    "color: #5b70d7",
    eventPayload
  );
}
var logErrorFormatter_default = logTrackError;

// src/utils/objKeysCaseConverter.ts
var convertKeysFromCamelToSnakeCase = (obj) => Object.keys(obj).reduce((acc, key) => {
  const newKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
  acc[newKey] = obj[key];
  return acc;
}, {});
var objKeysCaseConverter_default = convertKeysFromCamelToSnakeCase;

// src/utils/splitLocale.ts
var EN_GB = "en-gb";
var EN = "en";
var fallback = {
  locale: EN,
  languageCode: EN,
  countryCode: "INT"
};
var handlePayloadKeys = (payload, convertKeysToSnakeCase = false) => convertKeysToSnakeCase ? objKeysCaseConverter_default(payload) : payload;
var splitLocale = (locale, serverSide = false) => {
  if (!locale) return handlePayloadKeys(fallback, serverSide);
  const localeLC = locale.toLowerCase();
  const validLocale = localeLC.substring(0, 2) === EN && localeLC !== EN_GB ? EN : localeLC;
  const [languageCode, countryCode = fallback.countryCode] = validLocale.split("-");
  const payload = {
    locale: validLocale,
    languageCode: languageCode == null ? void 0 : languageCode.toLowerCase(),
    countryCode: countryCode == null ? void 0 : countryCode.toUpperCase()
  };
  return handlePayloadKeys(payload, serverSide);
};
var splitLocale_default = splitLocale;

// src/utils/splitLocaleSnakeCase.ts
var splitLocaleSnakeCase = (locale) => splitLocale_default(locale, true);
var splitLocaleSnakeCase_default = splitLocaleSnakeCase;

// src/utils/storage/StorageHelper.ts
var StorageHelper = class {
  constructor(storage) {
    this.storage = storage;
  }
  /**
   * Retrieves an item from the specified storage and parses it as JSON.
   * @param {string} key - The key of the item to retrieve.
   * @returns {T | null} The parsed item, or null if the item does not exist or parsing fails.
   */
  getItem(key) {
    const item = this.storage.getItem(key);
    if (item == null) return null;
    if (!item.startsWith("[") && !item.startsWith("{")) {
      return item;
    }
    try {
      return JSON.parse(item);
    } catch (e) {
      console.error("Error parsing JSON from storage", e);
      return null;
    }
  }
  /**
   * Stores an item in the specified storage as a JSON string.
   * @param {string} key - The key under which to store the item.
   * @param {T} value - The item to store.
   */
  setItem(key, value) {
    const storageValue = typeof value === "string" ? value : JSON.stringify(value);
    this.storage.setItem(key, storageValue);
  }
  /**
   * Deletes an item associated with a specific key from the specified storage.
   * @param {string} key - The key of the item to delete.
   */
  removeItem(key) {
    this.storage.removeItem(key);
  }
};
var StorageHelper_default = StorageHelper;
var storageHelperArgsValid = (isCLientSide, storageKey) => {
  if (!isCLientSide) {
    console.warn("It is not client side");
    return false;
  }
  if (!storageKey) {
    console.warn("The key to get or set value in storage is not valid");
    return false;
  }
  return isCLientSide && !!storageKey;
};

// src/utils/storage/getFromLocalStorage.ts
var getFromLocalStorage = (key) => {
  if (!storageHelperArgsValid(isBrowser_default, key) || !key) {
    return null;
  }
  const helper = new StorageHelper_default(localStorage);
  return helper.getItem(key);
};
var getFromLocalStorage_default = getFromLocalStorage;

// src/utils/storage/getFromSessionStorage.ts
var getFromSessionStorage = (key) => {
  if (!storageHelperArgsValid(isBrowser_default, key) || !key) {
    return null;
  }
  const helper = new StorageHelper_default(sessionStorage);
  return helper.getItem(key);
};
var getFromSessionStorage_default = getFromSessionStorage;

// src/utils/storage/removeFromLocalStorage.ts
var removeFromLocalStorage = (key) => {
  if (!storageHelperArgsValid(isBrowser_default, key) || !key) {
    return;
  }
  const helper = new StorageHelper_default(localStorage);
  helper.removeItem(key);
};
var removeFromLocalStorage_default = removeFromLocalStorage;

// src/utils/storage/removeFromSessionStorage.ts
var removeFromSessionStorage = (key) => {
  if (!storageHelperArgsValid(isBrowser_default, key) || !key) {
    return;
  }
  const helper = new StorageHelper_default(sessionStorage);
  helper.removeItem(key);
};
var removeFromSessionStorage_default = removeFromSessionStorage;

// src/utils/storage/setToLocalStorage.ts
var setToLocalStorage = (key, value) => {
  if (!storageHelperArgsValid(isBrowser_default, key) || !key) {
    return;
  }
  const helper = new StorageHelper_default(localStorage);
  helper.setItem(key, value);
};
var setToLocalStorage_default = setToLocalStorage;

// src/utils/storage/setToSessionStorage.ts
var setToSessionStorage = (key, value) => {
  if (!storageHelperArgsValid(isBrowser_default, key) || !key) {
    return;
  }
  const helper = new StorageHelper_default(sessionStorage);
  helper.setItem(key, value);
};
var setToSessionStorage_default = setToSessionStorage;

// src/utils/applyPayloadFilters.ts
var withPayloadFilters = (payload, [...filters]) => {
  const filteredPayload = __spreadValues({}, payload);
  filters.forEach((filter) => {
    delete filteredPayload[filter];
  });
  return filteredPayload;
};
var applyPayloadFilters_default = withPayloadFilters;
var getEventLiterals = (schema) => {
  const eventShape = schema.shape.event;
  if (eventShape instanceof ZodUnion) {
    return eventShape._def.options.map(
      (option) => option._def.value
    );
  } else if (eventShape instanceof ZodLiteral) {
    return [eventShape._def.value];
  }
  return [];
};
var getEventLiterals_default = getEventLiterals;

// src/utils/overwriteDefaultPageProps.ts
var overwriteDefaultPageProps = (_a) => {
  var _b = _a, {
    properties,
    options
  } = _b, rest = __objRest(_b, [
    "properties",
    "options"
  ]);
  return __spreadProps(__spreadValues({}, rest), {
    properties: __spreadProps(__spreadValues({}, properties), {
      path: void 0,
      url: void 0,
      title: void 0
    }),
    options: __spreadProps(__spreadValues({}, options), {
      page: {
        path: properties == null ? void 0 : properties.pagePath,
        url: properties == null ? void 0 : properties.url,
        title: properties == null ? void 0 : properties.pageTitle
      }
    })
  });
};
var overwriteDefaultPageProps_default = overwriteDefaultPageProps;

// src/utils/type_brand/assertEventPayload.ts
var EventPayloadError = class extends Error {
  constructor(e) {
    super(e);
    this.name = "AssertEventPayloadError";
  }
};
function hasEventProperty(payload) {
  return (payload == null ? void 0 : payload.event) !== void 0;
}
function assertEventPayload(payload) {
  if (!hasEventProperty(payload))
    throw new EventPayloadError('Payload is missing the "event" key.');
  const { event } = payload;
  if (typeof event !== "string" || event === "")
    throw new EventPayloadError(
      'The "event" key must have a non-empty string value.'
    );
}
var assertEventPayload_default = assertEventPayload;

// src/utils/validateUserId.ts
var validateUserId = (userIdentity) => {
  const { anonymous_id, user_id } = userIdentity;
  if (!anonymous_id && !user_id) {
    throw new TypeError(
      "anonymous_id or user_id should be given in the segment call payload"
    );
  }
};
var validateUserId_default = validateUserId;

// src/stores/trackingData.ts
var trackingDataSchema = z.object({
  bookingEngine: z.string().optional(),
  businessArea: z.string().optional(),
  countryCode: z.string().optional(),
  languageCode: z.string().optional(),
  pageId: z.string().optional()
});
var DFDS_TRACKING_KEY = "DFDStracking";
var PAGE_CONTEXT_KEY = "pageContext";
var TrackingDataStoreHandler = class {
  constructor() {
    this.trackingData = {};
    this.setTrackingData = (data) => {
      if (isBrowser_default) {
        const newTrackingData = JSON.stringify(__spreadValues(__spreadValues({}, this.getTrackingData()), data));
        [PAGE_CONTEXT_KEY, DFDS_TRACKING_KEY].forEach(
          (key) => sessionStorage.setItem(key, newTrackingData)
        );
        return;
      }
      this.trackingData = __spreadValues(__spreadValues({}, this.trackingData), data);
    };
  }
  getTrackingData() {
    if (isBrowser_default) {
      const storage = sessionStorage.getItem(DFDS_TRACKING_KEY);
      try {
        const trackingDataUnvalidated = JSON.parse(storage || "{}");
        return trackingDataSchema.parse(trackingDataUnvalidated);
      } catch (e) {
      }
    }
    return this.trackingData;
  }
};
var trackingDataStore = new TrackingDataStoreHandler();
var getTrackingData = trackingDataStore.getTrackingData;
var setTrackingData = trackingDataStore.setTrackingData;

// src/errorClasses/DispatchError.ts
var DispatchError = class extends Error {
  constructor(e) {
    super(
      `Dispatcher:	${e.dispatcher}
Type:		${e.type}
Message:	${e.message}`
    );
    this.name = "DispatchError";
  }
};
var DispatchError_default = DispatchError;

// src/errorClasses/MutatingMiddlewareError.ts
var MutatingMiddlewareError = class extends Error {
  constructor(e) {
    super(
      `Mutating middleware:	${e.mutatingMiddleware}
Type:		${e.type}
Message:	${e.message}`
    );
    this.name = "MutatingMiddlewareError";
  }
};
var MutatingMiddlewareError_default = MutatingMiddlewareError;

// src/errorClasses/NonMutatingMiddlewareError.ts
var MutatingMiddlewareError2 = class extends Error {
  constructor(e) {
    super(
      `Non-mutating middleware:	${e.nonMutatingMiddleware}
Type:		${e.type}
Message:	${e.message}`
    );
    this.name = "NonMutatingMiddlewareError";
  }
};
var NonMutatingMiddlewareError_default = MutatingMiddlewareError2;

// src/dispatchers/dataLayer/dataLayerPush.ts
var eventFilters = {
  formSubmit: [
    "companyPhoneNumber",
    "companyEmail",
    "customerEmail",
    "firstName",
    "lastName",
    "form_payload"
  ],
  auth_success: ["user_id", "user_email", "user_first_name"]
};
var dataLayerPush = async (eventPayload) => {
  try {
    if (isBrowser_default && Object.keys(eventPayload).length > 0) {
      const filters = eventFilters[eventPayload.event];
      if (filters) {
        eventPayload = applyPayloadFilters_default(eventPayload, filters);
      }
      ;
      (window.dataLayer || (window.dataLayer = [])).push(eventPayload);
    }
  } catch (e) {
    if (e instanceof Error)
      throw new DispatchError_default({
        dispatcher: "dataLayerPush",
        type: "dataLayerPushFail",
        message: e.message
      });
  }
};
var dataLayerPush_default = dataLayerPush;
var authClickNavSchema = z.object({
  event: z.literal("auth_click_nav"),
  locale: z.string(),
  application_id: z.string(),
  eng: z.union([
    z.literal("switch_to_create"),
    z.literal("password_forgot"),
    z.literal("password_resend_code")
  ]),
  eng_action: z.literal("select"),
  page_referrer: z.string()
});
var authClickRequestSchema = z.object({
  event: z.literal("auth_click_request"),
  locale: z.string(),
  application_id: z.string(),
  eng: z.union([z.literal("user_login"), z.literal("user_create")]),
  eng_action: z.literal("submit"),
  page_referrer: z.string()
});
var authErrorSchema = z.object({
  event: z.literal("auth_error"),
  locale: z.string(),
  application_id: z.string(),
  eng: z.union([
    z.literal("user_login"),
    z.literal("user_create"),
    z.literal("password_update")
  ]),
  eng_action: z.literal("response"),
  page_referrer: z.string(),
  status: z.literal("error"),
  status_code: z.number().optional(),
  error_type: z.union([z.literal("api_error"), z.literal("validation_error")]),
  error_code: z.string().optional(),
  error_message: z.string(),
  error_correlation_id: z.string().optional()
});
var authInitSchema = z.object({
  event: z.literal("auth_init"),
  locale: z.string(),
  application_id: z.string(),
  eng: z.union([z.literal("user_login"), z.literal("user_create")]),
  eng_action: z.literal("initialize"),
  page_referrer: z.string()
});
var authUpdatePasswordSchema = z.object({
  event: z.literal("auth_update_password"),
  locale: z.string(),
  application_id: z.string(),
  eng: z.union([
    z.literal("password_reset_request"),
    z.literal("password_reset_verify"),
    z.literal("password_change")
  ]),
  eng_action: z.literal("submit"),
  page_referrer: z.string()
});
var authSuccessSchema = z.object({
  event: z.literal("auth_success"),
  locale: z.string(),
  application_id: z.string(),
  eng: z.union([
    z.literal("user_login"),
    z.literal("user_create"),
    z.literal("password_update")
  ]),
  eng_action: z.literal("response"),
  page_referrer: z.string(),
  user_id: z.string(),
  user_email: z.string(),
  user_first_name: z.string().optional()
});
var optimizeActivateSchema = z.object({
  event: z.literal("optimize.activate")
});
var buttonClickSchema = z.object({
  event: z.literal("buttonClick"),
  eventAction: z.string(),
  clickedElementText: z.string()
});
var formValidationErrorSchema = z.object({
  event: z.literal("formValidationError"),
  errorMessage: z.string(),
  errorElementName: z.string(),
  errorType: z.literal("validation")
});
var discountSchemeSchema = z.object({
  event: z.union([
    z.literal("applyDiscountScheme"),
    z.literal("removeDiscountScheme"),
    z.literal("clickDiscountScheme"),
    z.literal("discountScheme")
  ]),
  affiliation: z.literal("nectar"),
  discountValue: z.number().optional()
});
var clickElementSchema = z.object({
  event: z.literal("clickElement"),
  clickedElementText: z.string()
});
var formSubmitSchema = z.object({
  event: z.literal("formSubmit"),
  eventOrigin: z.literal("ukapi"),
  formName: z.string(),
  formID: z.string(),
  currentLocale: z.string(),
  isAgent: z.boolean(),
  mldfdsRef: z.string().optional(),
  fullRouteCode: z.string().optional(),
  routeCode: z.string().optional(),
  errorType: z.string().optional(),
  passengerFormNumber: z.number().optional()
});
var formSubmitErrorSchema = z.object({
  event: z.literal("formSubmit"),
  eventOrigin: z.literal("ukapi"),
  errorType: z.literal("APIError"),
  formName: z.string(),
  formID: z.string(),
  currentLocale: z.string(),
  isAgent: z.boolean(),
  passengerFormNumber: z.number().optional(),
  errorMessage: z.string().optional(),
  errorMessageId: z.string().optional(),
  errorCorrelationId: z.string().optional()
});
var formValidationErrorPassengerSchema = z.object({
  event: z.literal("formValidationError"),
  eventOrigin: z.literal("ukapi"),
  formName: z.string(),
  formID: z.string(),
  currentLocale: z.string(),
  isAgent: z.boolean(),
  errorElementName: z.string().optional(),
  errorMessage: z.string().optional(),
  errorMessageId: z.string().optional()
});
var verifyPassengerDetailsSchema = z.object({
  event: z.literal("verifyPassengerDetails"),
  eventOrigin: z.literal("ukapi"),
  formName: z.literal("passenger_details"),
  formID: z.literal("passenger_details"),
  currentLocale: z.string(),
  passengerFormNumber: z.number(),
  outboundPassengers: z.number(),
  isAgent: z.boolean()
});
var bookingTrackingFutureFreightSchema = z.object({
  bookingEngine: z.literal("futureFreight"),
  bookShieldCounter: z.union([z.string(), z.number()]).optional(),
  businessArea: z.literal("freight"),
  cargoType: z.string().optional(),
  companyUserId: z.string().optional(),
  countryCode: z.string(),
  currentLocale: z.string(),
  customerId: z.union([z.string(), z.number()]).optional(),
  departureDate: z.string(),
  drivers: z.string().optional(),
  driversCount: z.union([z.string(), z.number()]).optional(),
  driversDetails: z.array(
    z.object({
      completedDetails: z.boolean().optional(),
      detailsAvailable: z.object({}).optional(),
      driverIdentifier: z.string().optional()
    })
  ).optional(),
  driversDetailsCompleted: z.boolean().optional(),
  driversDetailsUpdated: z.boolean().optional(),
  event: z.union([
    z.literal("createBooking"),
    z.literal("draftBooking"),
    z.literal("deleteBooking"),
    z.literal("viewBooking"),
    z.literal("viewBookingEdit"),
    z.literal("editBooking"),
    z.literal("duplicateBooking"),
    z.literal("saveBooking"),
    z.literal("cancelEditBooking")
  ]),
  languageCode: z.string(),
  market: z.string(),
  mldfdsRef: z.string().optional(),
  routeCode: z.string(),
  standardLength: z.union([z.string(), z.number()]),
  unitType: z.string(),
  unloCode: z.string(),
  userID: z.string().optional()
});
var companyChangeTrackingSchema = z.object({
  event: z.literal("companyChange"),
  customerId: z.string()
});
var introTrackingSchema = z.object({
  event: z.literal("skipIntro")
});
var virtualPageTrackingFutureFreightSchema = z.object({
  event: z.literal("vpv"),
  bookingEngine: z.literal("futureFreight"),
  countryCode: z.string(),
  languageCode: z.string(),
  contentfulMarker: z.string(),
  statusCode: z.string(),
  contentfulId: z.string(),
  pagePath: z.string(),
  businessArea: z.string(),
  pageTitle: z.string(),
  userID: z.string().optional(),
  companyUserId: z.string().optional(),
  customerId: z.string().optional()
});
var searchTrackingSchema = z.object({
  event: z.literal("searchOccurred"),
  bookingEngine: z.literal("futureFreight"),
  businessArea: z.literal("freight"),
  currentLocale: z.string(),
  searchResultCount: z.number(),
  countryCode: z.string(),
  languageCode: z.string(),
  searchFilter: z.string().optional(),
  searchKeyword: z.string().optional(),
  flow: z.string().optional(),
  userID: z.string().optional(),
  customerEmail: z.string().optional()
});
var tableSortTrackingSchema = z.object({
  event: z.literal("tableSort"),
  sortTitle: z.string(),
  sortDirection: z.union([z.literal("ascending"), z.literal("descending")])
});
var trackFileUploadSchema = z.object({
  event: z.literal("uploadFile"),
  uploadCategory: z.string(),
  uploadStatus: z.string(),
  countryCode: z.string(),
  bookingEngine: z.string().optional(),
  userID: z.string().optional(),
  companyUserId: z.string().optional(),
  customerId: z.string().optional()
});
var trackFileDownloadSchema = z.object({
  event: z.literal("downloadFile"),
  downloadType: z.string(),
  downloadedBookingsCount: z.string(),
  countryCode: z.string(),
  bookingEngine: z.string().optional(),
  userID: z.string().optional(),
  companyUserId: z.string().optional(),
  customerId: z.string().optional()
});
var trackInvoiceDownloadSchema = z.object({
  event: z.literal("downloadInvoice"),
  countryCode: z.string(),
  bookingEngine: z.string().optional(),
  userID: z.string().optional(),
  companyUserId: z.string().optional(),
  customerId: z.string().optional()
});
var loginComponentSchema = z.object({
  event: z.union([
    z.literal("userLogin"),
    z.literal("changePassword"),
    z.literal("forgotPassword"),
    z.literal("userRegister"),
    z.literal("newsletterSignUp"),
    z.literal("overlayOpen")
  ]),
  locale: z.string(),
  bookingEngine: z.union([
    z.literal("dotcom"),
    z.literal("tourOperator"),
    z.literal("futureFreight"),
    z.literal("seabookFlow"),
    z.literal("directportal"),
    z.literal("myprofile"),
    z.literal("PAXOB")
  ]),
  businessArea: z.string().optional(),
  applicationID: z.string().optional(),
  status: z.union([z.literal("success"), z.literal("fail")]).optional(),
  userID: z.string().optional(),
  step: z.union([
    z.literal("clickForgotPassword"),
    z.literal("sendForgotPasswordEmail"),
    z.literal("clickDidNotGetEmail"),
    z.literal("clickCustomerSupport"),
    z.literal("clickResendEmail"),
    z.literal("verify")
  ]).optional(),
  errorMessageId: z.string().optional(),
  errorMessage: z.string().optional(),
  errorType: z.union([z.literal("APIError"), z.literal("validation")]).optional(),
  errorCorrelationId: z.string().optional(),
  fieldName: z.string().optional(),
  overlayType: z.string().optional()
});
var noBookingsFoundSchema = z.object({
  event: z.literal("noBookings")
});
var buttonClickedSchema = z.object({
  event: z.literal("buttonClicked"),
  eventAction: z.string(),
  bookingEngine: z.union([
    z.literal("futureFreight"),
    z.literal("dotcom"),
    z.literal("tourOperator"),
    z.literal("seabookFlow"),
    z.literal("directportal"),
    z.literal("myprofile")
  ]),
  businessArea: z.string(),
  countryCode: z.string(),
  flow: z.string().optional(),
  userID: z.string().optional(),
  customerEmail: z.string().optional()
});
var originalLocationSchema = z.object({
  event: z.literal("originalLocation"),
  originalLocation: z.string()
});
var videoSchema = z.object({
  event: z.literal("video"),
  videoName: z.string(),
  languageCode: z.string(),
  countryCode: z.string(),
  videoStatus: z.union([
    z.literal("Play"),
    z.literal("Pause"),
    z.literal("End")
  ])
});
var countrySwitchSchema = z.object({
  event: z.literal("countrySwitch"),
  currentLocale: z.string(),
  newLocale: z.string(),
  languageOption: z.string()
});
var overlayTrackingSchema = z.object({
  event: z.literal("vpv"),
  pagePath: z.string(),
  pageTitle: z.string(),
  countryCode: z.string(),
  languageCode: z.string(),
  contentfulMarker: z.string(),
  businessArea: z.string(),
  overlayContentfulEntryId: z.string(),
  overlayContentfulContentType: z.string(),
  statusCode: z.string(),
  contentfulId: z.string(),
  bookingEngine: z.string(),
  url: z.string().optional()
});
var accordionClickSchema = z.object({
  event: z.literal("accordionClick"),
  accordionTitle: z.string(),
  accordionContentfulId: z.string()
});
var newsletterSubscribeSchema = z.object({
  event: z.literal("newsletterSignUp"),
  status: z.union([z.literal("success"), z.literal("fail")]),
  bookingEngine: z.string().optional(),
  applicationID: z.string().optional()
});
var newsletterUnsubscribeSchema = z.object({
  event: z.literal("newsletterUnsubscribed"),
  emailCampaignId: z.string()
});
var browserNotSupportedSchema = z.object({
  event: z.literal("browserNotSupported")
});
var cookieConsentSchema = z.object({
  event: z.union([
    z.literal("cookie_cat_necessary"),
    z.literal("cookie_cat_functional"),
    z.literal("cookie_cat_statistic"),
    z.literal("cookie_cat_marketing")
  ])
});
var mlNewAddressSchema = z.object({
  event: z.literal("mlNewAddress"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  mldfdsRef: z.string().optional()
});
var audienceExclusionSchema = z.object({
  event: z.literal("audienceExclusion"),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  audienceExclusion: z.boolean(),
  contentfulId: z.union([
    z.literal("2-CompanyDetails"),
    z.literal("2B-PrivateShipment")
  ])
});
var formSubmitAPIFailSchema = z.object({
  event: z.literal("formSubmit"),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  errorType: z.literal("APIError"),
  errorMessage: z.string().optional(),
  formID: z.literal("b2b_lead_do_directportal_signup"),
  formName: z.literal("b2b_lead_do_directportal_signup"),
  status: z.literal("fail")
});
var formSubmitSuccessSchema = z.object({
  event: z.literal("formSubmit"),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  currentAssociateName: z.literal("APIError").optional(),
  currentAssociateVatNumber: z.string().optional(),
  formID: z.literal("b2b_lead_do_directportal_signup"),
  formName: z.literal("b2b_lead_do_directportal_signup"),
  status: z.literal("fail")
});
var formSubmitValidationFailSchema = z.object({
  event: z.literal("formSubmit"),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  errorType: z.literal("validation"),
  formID: z.literal("b2b_lead_do_directportal_signup"),
  formName: z.literal("b2b_lead_do_directportal_signup"),
  status: z.literal("fail")
});
var userRegisterLoginSchema = z.object({
  event: z.literal("userRegisterLogin"),
  businessArea: z.literal("logistics"),
  bookingEngine: z.union([
    z.literal("customsclearance"),
    z.literal("directportal")
  ]),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  userID: z.string().optional(),
  customerEmail: z.string().optional(),
  applicationID: z.string().optional(),
  flow: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  locale: z.string(),
  status: z.string()
});
var bookingAccessRequestedSchema = z.object({
  event: z.literal("bookingAccessRequested"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  quoteID: z.string().optional(),
  bookingOrigin: z.union([z.literal("instant"), z.literal("semi-instant")]),
  quoteExpire: z.string().optional(),
  price: z.number().optional(),
  currencyCode: z.string().optional(),
  loadType: z.union([z.literal("FULL_LOAD"), z.literal("PART_LOAD")]).optional(),
  pickupCountry: z.string().optional(),
  pickupCity: z.string().optional(),
  deliveryCountry: z.string().optional(),
  deliveryCity: z.string().optional(),
  equipmentType: z.union([
    z.literal("COND"),
    z.literal("CONR"),
    z.literal("SP_FLAT"),
    z.literal("SP_MFL"),
    z.literal("TRA"),
    z.literal("TRAM"),
    z.literal("TRAR"),
    z.literal("not_set")
  ]),
  subBookings: z.literal("1").optional(),
  numberPackage: z.string()
});
var clearFormStateSchema = z.object({
  event: z.literal("clearFormState"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  form: z.union([z.literal("Bookingform"), z.literal("QuoteEditor")]).optional(),
  status: z.literal("Form state was cleared").optional()
});
var clearTemplateSchema = z.object({
  event: z.literal("clearTemplate"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional()
});
var companyChangeSchema = z.object({
  event: z.literal("companyChange"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional()
});
var createTemplateSchema = z.object({
  event: z.literal("createTemplate"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional()
});
var dfdsDirectSearchSchema = z.object({
  event: z.literal("DFDS Direct | Search"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional()
});
var displayBannerSchema = z.object({
  event: z.literal("displayBanner"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  message: z.string()
});
var errorSchema = z.object({
  event: z.literal("error"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  rejectReason: z.string()
});
var errorOccuredSchema = z.object({
  event: z.literal("errorOccured"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  errorName: z.literal("quote_creation_failed"),
  errorType: z.literal("APIError"),
  errorMessage: z.string().optional(),
  contentfulId: z.string().optional(),
  statusCode: z.string().optional()
});
var formValidationError2Schema = z.object({
  event: z.literal("formValidationError"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  fieldName: z.string(),
  fieldMessage: z.string()
});
var formValidationErrorCargoSchema = z.object({
  event: z.literal("formValidationError"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  fieldName: z.string(),
  fieldMessage: z.string()
});
var mlAddPackageSchema = z.object({
  event: z.literal("mlAddPackage"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional()
});
var mlClearSearchSchema = z.object({
  event: z.literal("mlClearSearch"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional()
});
var mlClickNewBookingSchema = z.object({
  event: z.literal("mlClickNewBooking"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional()
});
var mlClickNewClearanceSchema = z.object({
  event: z.literal("mlClickNewClearance"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional()
});
var mlClickRefreshBookingsListSchema = z.object({
  event: z.literal("mlClickRefreshBookingsList"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional()
});
var mlDeleteBookingSchema = z.object({
  event: z.literal("mlDeleteBooking"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional()
});
var mlDeletePackageSchema = z.object({
  event: z.literal("mlDeletePackage"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional()
});
var mlDownloadWindowSchema = z.object({
  event: z.literal("mlDownloadWindow"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  mldfdsRef: z.string().optional()
});
var mlinstantRequestSchema = z.object({
  event: z.literal("bookingAccessRequested"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  quoteID: z.string().optional(),
  bookingOrigin: z.union([z.literal("instant"), z.literal("semi-instant")]),
  quoteExpire: z.string().optional(),
  price: z.number().optional(),
  currencyCode: z.string().optional(),
  loadType: z.union([z.literal("FULL_LOAD"), z.literal("PART_LOAD")]).optional(),
  pickupCountry: z.string().optional(),
  pickupCity: z.string().optional(),
  deliveryCountry: z.string().optional(),
  deliveryCity: z.string().optional(),
  equipmentType: z.union([
    z.literal("COND"),
    z.literal("CONR"),
    z.literal("SP_FLAT"),
    z.literal("SP_MFL"),
    z.literal("TRA"),
    z.literal("TRAM"),
    z.literal("TRAR"),
    z.literal("not_set")
  ]),
  subBookings: z.literal("1").optional(),
  numberPackage: z.string()
});
var mlSortBySchema = z.object({
  event: z.literal("mlSortBy"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  mlSortBy: z.string().optional()
});
var mlUploadSchema = z.object({
  event: z.union([z.literal("uploadError"), z.literal("mlUpload")]),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  uploadStatus: z.string().optional(),
  category: z.string().optional(),
  bookingId: z.string().optional(),
  bookingStatus: z.string().optional(),
  type: z.string().optional(),
  error: z.object({
    message: z.string().optional(),
    code: z.string().optional()
  }).optional()
});
var selectTemplateSchema = z.object({
  event: z.literal("selectTemplate"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional()
});
var updateTemplateSchema = z.object({
  event: z.literal("updateTemplate"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional()
});
var mlBookingSchema = z.object({
  event: z.union([
    z.literal("mlDraftBooking"),
    z.literal("mlBookingUpdated"),
    z.literal("mlBookingWithdraw"),
    z.literal("mlViewBooking")
  ]),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  mlOrderStatus: z.string(),
  mldfdsRef: z.string().optional(),
  bookingOrigin: z.union([
    z.literal("instant"),
    z.literal("semi-instant"),
    z.literal("manual"),
    z.literal("agreement")
  ]),
  quoteID: z.string().optional(),
  price: z.string().optional(),
  quoteExpire: z.string().optional(),
  currencyCode: z.string().optional(),
  loadType: z.union([z.literal("full_load"), z.literal("part_load")]),
  transportTerms: z.string(),
  equipmentType: z.string(),
  scheduleId: z.string().optional(),
  isScheduleActive: z.string().optional(),
  serviceDelivery: z.union([
    z.literal("neutral_delivery"),
    z.literal("not_set")
  ]),
  pickUpDate: z.string(),
  dropOffDate: z.string(),
  pickupCountry: z.string(),
  pickupCity: z.string(),
  pickupPostCode: z.string(),
  deliveryCountry: z.string().optional(),
  deliveryCity: z.string().optional(),
  deliveryPostCode: z.string().optional(),
  numberPackage: z.string().optional(),
  subBookings: z.number().optional(),
  customsClearance: z.boolean().optional(),
  customsClearanceRep: z.string().optional(),
  customsFields: z.string()
});
var mlDuplicateBookingSchema = z.object({
  event: z.literal("mlDuplicateBooking"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  mldfdsRef: z.number().optional()
});
var mlEditBookingSchema = z.object({
  event: z.literal("mlEditBooking"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  mldfdsRef: z.number().optional()
});
var mlCustomsBookingSchema = z.object({
  event: z.literal("mlCustomsBooking"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.string(),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  bookingOrigin: z.literal("agreement").optional(),
  bookingTemplate: z.boolean().optional(),
  deliveryCountry: z.string().optional(),
  equipmentType: z.union([
    z.literal("truckAndTrailer"),
    z.literal("trailer"),
    z.literal("container")
  ]).optional(),
  mldfdsRef: z.number().optional(),
  mlOrderStatus: z.union([
    z.literal("ACCEPTED"),
    z.literal("CLEARED"),
    z.literal("PENDING"),
    z.literal("REJECTED")
  ]).optional(),
  pickUpDate: z.string().optional(),
  pickupCountry: z.string().optional(),
  unloCode: z.string()
});
var mlDeleteCustomsSchema = z.object({
  event: z.literal("mlDeleteCustoms"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.string(),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  bookingOrigin: z.literal("agreement").optional(),
  bookingTemplate: z.boolean().optional(),
  deliveryCountry: z.string().optional(),
  equipmentType: z.union([
    z.literal("truckAndTrailer"),
    z.literal("trailer"),
    z.literal("container")
  ]).optional(),
  mldfdsRef: z.number().optional(),
  mlOrderStatus: z.union([
    z.literal("ACCEPTED"),
    z.literal("CLEARED"),
    z.literal("PENDING"),
    z.literal("REJECTED")
  ]).optional(),
  pickUpDate: z.string().optional(),
  pickupCountry: z.string().optional(),
  unloCode: z.string()
});
var mlEditCustomsSchema = z.object({
  event: z.literal("mlEditCustoms"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.string(),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  bookingOrigin: z.literal("agreement").optional(),
  bookingTemplate: z.boolean().optional(),
  deliveryCountry: z.string().optional(),
  equipmentType: z.union([
    z.literal("truckAndTrailer"),
    z.literal("trailer"),
    z.literal("container")
  ]).optional(),
  mldfdsRef: z.number().optional(),
  mlOrderStatus: z.union([
    z.literal("ACCEPTED"),
    z.literal("CLEARED"),
    z.literal("PENDING"),
    z.literal("REJECTED")
  ]).optional(),
  pickUpDate: z.string().optional(),
  pickupCountry: z.string().optional(),
  unloCode: z.string()
});
var mlViewCustomsSchema = z.object({
  event: z.literal("mlViewCustoms"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.string(),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  bookingOrigin: z.literal("agreement").optional(),
  bookingTemplate: z.boolean().optional(),
  deliveryCountry: z.string().optional(),
  equipmentType: z.union([
    z.literal("truckAndTrailer"),
    z.literal("trailer"),
    z.literal("container")
  ]).optional(),
  mldfdsRef: z.number().optional(),
  mlOrderStatus: z.union([
    z.literal("ACCEPTED"),
    z.literal("CLEARED"),
    z.literal("PENDING"),
    z.literal("REJECTED")
  ]).optional(),
  pickUpDate: z.string().optional(),
  pickupCountry: z.string().optional(),
  unloCode: z.string()
});
var mlDownloadSchema = z.object({
  event: z.literal("mlDownload"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  mldfdsRef: z.string().optional(),
  mlOrderStatus: z.string().optional(),
  category: z.union([
    z.literal("CATCH"),
    z.literal("CHED"),
    z.literal("COMINV"),
    z.literal("CSTMS"),
    z.literal("E2"),
    z.literal("EMAIL"),
    z.literal("EXPDEC"),
    z.literal("HAZ"),
    z.literal("HCERT"),
    z.literal("IMPDEC"),
    z.literal("INVOICE"),
    z.literal("IPAFFS"),
    z.literal("PACK"),
    z.literal("POD"),
    z.literal("REPORT"),
    z.literal("T1"),
    z.literal("T2"),
    z.literal("TRNSP")
  ]).optional(),
  bookingId: z.boolean().optional(),
  bookingStatus: z.string().optional()
});
var mlDownloadinvoiceSchema = z.object({
  event: z.literal("mlDownloadinvoice"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  mldfdsRef: z.string().optional(),
  mlOrderStatus: z.string().optional(),
  category: z.union([
    z.literal("CATCH"),
    z.literal("CHED"),
    z.literal("COMINV"),
    z.literal("CSTMS"),
    z.literal("E2"),
    z.literal("EMAIL"),
    z.literal("EXPDEC"),
    z.literal("HAZ"),
    z.literal("HCERT"),
    z.literal("IMPDEC"),
    z.literal("INVOICE"),
    z.literal("IPAFFS"),
    z.literal("PACK"),
    z.literal("POD"),
    z.literal("REPORT"),
    z.literal("T1"),
    z.literal("T2"),
    z.literal("TRNSP")
  ]).optional(),
  bookingId: z.boolean().optional(),
  bookingStatus: z.string().optional()
});
var mlDownloadPodSchema = z.object({
  event: z.literal("mlDownloadPod"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  mldfdsRef: z.string().optional(),
  mlOrderStatus: z.string().optional(),
  category: z.union([
    z.literal("CATCH"),
    z.literal("CHED"),
    z.literal("COMINV"),
    z.literal("CSTMS"),
    z.literal("E2"),
    z.literal("EMAIL"),
    z.literal("EXPDEC"),
    z.literal("HAZ"),
    z.literal("HCERT"),
    z.literal("IMPDEC"),
    z.literal("INVOICE"),
    z.literal("IPAFFS"),
    z.literal("PACK"),
    z.literal("POD"),
    z.literal("REPORT"),
    z.literal("T1"),
    z.literal("T2"),
    z.literal("TRNSP")
  ]).optional(),
  bookingId: z.boolean().optional(),
  bookingStatus: z.string().optional()
});
var mlCreateScheduleSchema = z.object({
  event: z.literal("mlCreateSchedule"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  scheduleId: z.string().optional(),
  isActive: z.string().optional(),
  startDate: z.string().optional(),
  occurrenceDays: z.string(),
  scheduleTitle: z.string().optional(),
  equipmentType: z.string().optional(),
  loadType: z.string().optional(),
  dropOffDate: z.string().optional(),
  pickupCountry: z.string().optional(),
  pickupCity: z.string().optional(),
  pickupPostCode: z.string().optional(),
  deliveryCountry: z.string().optional(),
  deliveryCity: z.string().optional(),
  deliveryPostCode: z.string().optional(),
  numberPackage: z.string().optional()
});
var mlDeactivateScheduleSchema = z.object({
  event: z.literal("mlDeactivateSchedule"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  scheduleId: z.string().optional(),
  isActive: z.string().optional(),
  startDate: z.string().optional(),
  occurrenceDays: z.string(),
  scheduleTitle: z.string().optional(),
  equipmentType: z.string().optional(),
  loadType: z.string().optional(),
  dropOffDate: z.string().optional(),
  pickupCountry: z.string().optional(),
  pickupCity: z.string().optional(),
  pickupPostCode: z.string().optional(),
  deliveryCountry: z.string().optional(),
  deliveryCity: z.string().optional(),
  deliveryPostCode: z.string().optional(),
  numberPackage: z.string().optional()
});
var mlEditScheduleSchema = z.object({
  event: z.literal("mlEditSchedule"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  scheduleId: z.string().optional(),
  isActive: z.string().optional(),
  startDate: z.string().optional(),
  occurrenceDays: z.string(),
  scheduleTitle: z.string().optional(),
  equipmentType: z.string().optional(),
  loadType: z.string().optional(),
  dropOffDate: z.string().optional(),
  pickupCountry: z.string().optional(),
  pickupCity: z.string().optional(),
  pickupPostCode: z.string().optional(),
  deliveryCountry: z.string().optional(),
  deliveryCity: z.string().optional(),
  deliveryPostCode: z.string().optional(),
  numberPackage: z.string().optional()
});
var mlViewScheduleSchema = z.object({
  event: z.literal("mlViewSchedule"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  scheduleId: z.string().optional(),
  isActive: z.string().optional(),
  startDate: z.string().optional(),
  occurrenceDays: z.string(),
  scheduleTitle: z.string().optional(),
  equipmentType: z.string().optional(),
  loadType: z.string().optional(),
  dropOffDate: z.string().optional(),
  pickupCountry: z.string().optional(),
  pickupCity: z.string().optional(),
  pickupPostCode: z.string().optional(),
  deliveryCountry: z.string().optional(),
  deliveryCity: z.string().optional(),
  deliveryPostCode: z.string().optional(),
  numberPackage: z.string().optional()
});
var mlCreateBookingSchema = z.object({
  event: z.literal("mlCreateBooking"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  mldfdsRef: z.string(),
  customerEmail: z.string().optional(),
  loadType: z.string().optional(),
  transportTerms: z.string(),
  equipmentType: z.string().optional(),
  serviceDelivery: z.union([
    z.literal("neutral_delivery"),
    z.literal("not_set")
  ]),
  pickUpDate: z.string().optional(),
  dropOffDate: z.string().optional(),
  pickupCountry: z.string().optional(),
  pickupCity: z.string().optional(),
  pickupPostCode: z.string().optional(),
  deliveryCountry: z.string().optional(),
  deliveryCity: z.string().optional(),
  deliveryPostCode: z.string().optional(),
  numberPackage: z.string().optional(),
  bookingOrigin: z.string(),
  bookingTemplate: z.string().optional(),
  quoteID: z.string().optional(),
  price: z.string().optional(),
  quoteExpire: z.string().optional(),
  currencyCode: z.string().optional(),
  subBookings: z.string().optional(),
  customsClearance: z.string().optional(),
  customsClearanceRep: z.string().optional(),
  customsFields: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional()
});
var mlDraftBookingSchema = z.object({
  event: z.literal("mlDraftBooking"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  mldfdsRef: z.string(),
  customerEmail: z.string().optional(),
  loadType: z.string().optional(),
  transportTerms: z.string(),
  equipmentType: z.string().optional(),
  serviceDelivery: z.union([
    z.literal("neutral_delivery"),
    z.literal("not_set")
  ]),
  pickUpDate: z.string().optional(),
  dropOffDate: z.string().optional(),
  pickupCountry: z.string().optional(),
  pickupCity: z.string().optional(),
  pickupPostCode: z.string().optional(),
  deliveryCountry: z.string().optional(),
  deliveryCity: z.string().optional(),
  deliveryPostCode: z.string().optional(),
  numberPackage: z.string().optional(),
  bookingOrigin: z.string(),
  bookingTemplate: z.string().optional(),
  quoteID: z.string().optional(),
  price: z.string().optional(),
  quoteExpire: z.string().optional(),
  currencyCode: z.string().optional(),
  subBookings: z.string().optional(),
  customsClearance: z.string().optional(),
  customsClearanceRep: z.string().optional(),
  customsFields: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional()
});
var vpvGeneralSchema = z.object({
  event: z.literal("vpv"),
  statusCode: z.string(),
  pagePath: z.string(),
  pageTitle: z.string(),
  businessArea: z.string(),
  bookingEngine: z.string(),
  countryCode: z.string(),
  languageCode: z.string(),
  contentfulMarker: z.string(),
  contentfulId: z.string(),
  url: z.string().optional()
});
var vpvCustomerAccountSchema = z.object({
  event: z.literal("vpv"),
  bookingEngine: z.literal("myprofile"),
  businessArea: z.literal("passenger"),
  contentfulId: z.string(),
  pagePath: z.string(),
  pageTitle: z.string(),
  statusCode: z.literal("200"),
  currentLocale: z.string()
});
var vpvBookingExperienceSchema = z.object({
  event: z.literal("vpv"),
  bookingEngine: z.literal("PAXOB"),
  businessArea: z.literal("passenger"),
  countryCode: z.string(),
  isAgent: z.boolean(),
  languageCode: z.string(),
  localeCode: z.string(),
  currentLocale: z.string(),
  market: z.string(),
  salesOwner: z.string(),
  outboundDepartureDate: z.string().optional(),
  outboundDepartureTime: z.string().optional(),
  unloCode: z.string().optional(),
  routeCode: z.string().optional(),
  numberAdults: z.number().optional(),
  numberChildren: z.number().optional(),
  numberInfants: z.number().optional(),
  pagePath: z.string(),
  pageTitle: z.string(),
  contentfulId: z.string(),
  productCode: z.string().optional(),
  outboundProductCode: z.string().optional(),
  returnProductCode: z.string().optional(),
  returnDepartureDate: z.string().optional(),
  returnDepartureTime: z.string().optional(),
  returnUnloCode: z.string().optional(),
  returnRouteCode: z.string().optional(),
  session: z.string(),
  statusCode: z.number(),
  isReturnBooking: z.union([z.literal("yes"), z.literal("no")]),
  vehicleType: z.object({
    vehicleType: z.string(),
    trailerType: z.string().optional(),
    count: z.number()
  }),
  userID: z.string()
});
var vpvMfPassengerSchema = z.object({
  event: z.literal("vpv"),
  bookingEngine: z.literal("ukapi"),
  businessArea: z.literal("passenger"),
  contentfulId: z.literal("2afWpJZBKwAWXrpt1ZOtmT"),
  pageTitle: z.string().optional(),
  pagePath: z.string(),
  mldfdsRef: z.string(),
  currentLocale: z.string(),
  fullRouteCode: z.string(),
  routeCode: z.string(),
  outboundDepartureDate: z.string(),
  outboundPassengers: z.number(),
  passengersWithDetails: z.number().optional(),
  isAgent: z.boolean().optional()
});
var menuClickSchema = z.object({
  event: z.union([
    z.literal("globalMenuClicked"),
    z.literal("topMenuClicked"),
    z.literal("accordionClick")
  ]),
  navigationTitle: z.string(),
  navigationTitleContentfulId: z.string()
});
var productChangeSchema = z.object({
  event: z.union([z.literal("Product Added"), z.literal("Product Removed")]),
  product_id: z.string(),
  name: z.string(),
  price: z.number(),
  brand: z.string(),
  category: z.string(),
  variant: z.string(),
  quantity: z.number(),
  vehicle_type: z.string().optional(),
  vehicle_count: z.number().optional(),
  adult_count: z.number().optional(),
  children_count: z.number().optional(),
  children_age: z.array(z.number()).optional(),
  infant_count: z.number().optional(),
  pet_count: z.number().optional(),
  booking_engine: z.union([
    z.literal("dotcom"),
    z.literal("tourOperator"),
    z.literal("futureFreight"),
    z.literal("seabookFlow"),
    z.literal("seabookAmendment"),
    z.literal("directportal"),
    z.literal("myprofile"),
    z.literal("sqp"),
    z.literal("customsclearance"),
    z.literal("ukapi"),
    z.literal("PAXOB")
  ]),
  business_area: z.union([
    z.literal("passenger"),
    z.literal("logistics"),
    z.literal("freight"),
    z.literal("group")
  ]),
  locale: z.string(),
  page_id: z.string().optional(),
  flow: z.string().optional(),
  stage: z.union([
    z.literal("see"),
    z.literal("think"),
    z.literal("do"),
    z.literal("care")
  ]).optional(),
  additional_payload: z.object({}).optional(),
  list_id: z.string(),
  currency: z.string(),
  position: z.number(),
  cart_id: z.string(),
  email: z.string().optional(),
  ga_client_id: z.string().optional()
});
var bookingStatusSchema = z.object({
  event: z.literal("bookingStatus"),
  status: z.union([
    z.literal("pending"),
    z.literal("failed"),
    z.literal("success")
  ]),
  timing: z.union([z.literal("60sec"), z.literal("30min")]).optional()
});
var travelSearchInitializeSchema = z.object({
  event: z.literal("travel_search_initialize"),
  travel_search_session: z.string().optional(),
  trip_type: z.string().optional(),
  route_name: z.string().optional(),
  route_code: z.string().optional(),
  route_owner: z.number().optional(),
  is_vehicle_mandatory: z.boolean().optional(),
  is_pets_allowed_for_foot_passenger: z.boolean().optional(),
  max_number_of_pets: z.number().optional(),
  vehicle_code: z.string().optional(),
  vehicle_count: z.number().optional(),
  vehicle_category_code: z.string().optional(),
  vehicle_is_pets_allowed: z.boolean().optional(),
  vehicle_trailer_code: z.string().optional(),
  passenger_adults: z.number().optional(),
  passenger_children: z.number().optional(),
  passenger_pets: z.number().optional(),
  pets_selected: z.boolean().optional(),
  passenger_total: z.number().optional(),
  departure_date: z.string().optional(),
  return_date: z.string().optional(),
  trip_duration: z.number().optional()
});
var travelSearchResetSchema = z.object({
  event: z.literal("travel_search_reset"),
  travel_search_session: z.string().optional(),
  trip_type: z.string().optional(),
  route_name: z.string().optional(),
  route_code: z.string().optional(),
  route_owner: z.number().optional(),
  is_vehicle_mandatory: z.boolean().optional(),
  is_pets_allowed_for_foot_passenger: z.boolean().optional(),
  max_number_of_pets: z.number().optional(),
  vehicle_code: z.string().optional(),
  vehicle_category_code: z.string().optional(),
  vehicle_is_pets_allowed: z.boolean().optional(),
  vehicle_trailer_code: z.string().optional(),
  passenger_adults: z.number().optional(),
  passenger_children: z.number().optional(),
  passenger_pets: z.number().optional(),
  passenger_total: z.number().optional(),
  departure_date: z.string().optional(),
  return_date: z.string().optional(),
  trip_duration: z.number().optional()
});
var travelSearchSubmitSchema = z.object({
  event: z.literal("travel_search_submit"),
  travel_search_session: z.string().optional(),
  trip_type: z.string().optional(),
  route_name: z.string().optional(),
  route_code: z.string().optional(),
  route_owner: z.number().optional(),
  is_vehicle_mandatory: z.boolean().optional(),
  is_pets_allowed_for_foot_passenger: z.boolean().optional(),
  max_number_of_pets: z.number().optional(),
  vehicle_code: z.string().optional(),
  vehicle_count: z.number().optional(),
  vehicle_category_code: z.string().optional(),
  vehicle_is_pets_allowed: z.boolean().optional(),
  vehicle_trailer_code: z.string().optional(),
  passenger_adults: z.number().optional(),
  passenger_children: z.number().optional(),
  passenger_pets: z.number().optional(),
  pets_selected: z.boolean().optional(),
  passenger_total: z.number().optional(),
  departure_date: z.string().optional(),
  return_date: z.string().optional(),
  trip_duration: z.number().optional()
});
var travelSearchOptionsSchema = z.object({
  event: z.union([
    z.literal("travel_search_options_date"),
    z.literal("travel_search_options_trip_type"),
    z.literal("travel_search_options_route"),
    z.literal("travel_search_options_vehicle"),
    z.literal("travel_search_options_passenger"),
    z.literal("travel_search_options_multiple_vehicles")
  ]),
  travel_search_session: z.string().optional(),
  trip_type: z.string().optional(),
  route_name: z.string().optional(),
  route_code: z.string().optional(),
  route_owner: z.number().optional(),
  is_vehicle_mandatory: z.boolean().optional(),
  is_pets_allowed_for_foot_passenger: z.boolean().optional(),
  max_number_of_pets: z.number().optional(),
  vehicle_code: z.string().optional(),
  vehicle_count: z.number().optional(),
  vehicle_category_code: z.string().optional(),
  vehicle_is_pets_allowed: z.boolean().optional(),
  vehicle_trailer_code: z.string().optional(),
  passenger_adults: z.number().optional(),
  passenger_children: z.number().optional(),
  passenger_pets: z.number().optional(),
  pets_selected: z.boolean().optional(),
  passenger_total: z.number().optional(),
  departure_date: z.string().optional(),
  return_date: z.string().optional(),
  trip_duration: z.number().optional(),
  is_multiple_vehicles: z.boolean().optional()
});
var bookingAbandonedSchema = z.object({
  event: z.literal("bookingAbandoned").optional(),
  vehicle: z.object({}).optional(),
  departures: z.array(z.object({})).optional(),
  priceStructure: z.object({}).optional(),
  basketId: z.string().optional(),
  adult_count: z.number().optional(),
  children_count: z.number().optional(),
  children_age: z.array(z.number()).optional(),
  recover_basket_link: z.string().optional()
});
var careersVacanciesFilterSchema = z.object({
  event: z.literal("careers_vacancies_filter"),
  area: z.union([z.string(), z.array(z.string())]).optional(),
  location: z.union([z.string(), z.array(z.string())]).optional(),
  vacancies: z.number()
});
var careersVacancyClickSchema = z.object({
  event: z.literal("careers_vacancy_click"),
  vacancyId: z.string().optional(),
  area: z.string().optional(),
  location: z.string().optional(),
  offices: z.array(z.string()).optional(),
  vacancyTitle: z.string().optional(),
  vacancyUrl: z.string().optional(),
  vacancyPostingDate: z.string().optional()
});
var checkoutStepSchema = z.union([z.literal(1), z.literal(2)]);
var businessAreaSchema = z.union([
  z.literal("passenger"),
  z.literal("freight"),
  z.literal("logistics"),
  z.literal("freight_ferries_and_logistics"),
  z.literal("group"),
  z.literal("about")
]);
var vehicleTypeSchema = z.string();
var vehicleCountSchema = z.number();
var adultCountSchema = z.number();
var childrenCountSchema = z.number();
var childrenAgeSchema = z.array(z.number());
var infantCountSchema = z.number();
var petCountSchema = z.number();
var localeSchema = z.string();
var pageIdSchema = z.string();
var flowSchema = z.string();
var listIdSchema = z.string();
var cartIdSchema = z.array(z.unknown());
var emailSchema = z.string();
var gaClientIdSchema = z.string();
var productSchema = z.object({
  name: z.string(),
  id: z.string(),
  price: z.string(),
  brand: z.union([
    z.literal("SeabookExtra"),
    z.literal("SeabookDeparture"),
    z.literal("SeabookMeal"),
    z.literal("VisitGroupHotel"),
    z.literal("VisitGroupExtra"),
    z.literal("SeabookPet"),
    z.literal("SeabookCabin")
  ]),
  category: z.string(),
  variant: z.union([
    z.literal("Outbound"),
    z.literal("Return"),
    z.literal("Oneway")
  ]),
  quantity: z.string()
});
var removeFromCartSchema = z.object({
  event: z.literal("removeFromCart"),
  ecommerce: z.object({
    remove: z.object({
      products: z.array(productSchema)
    })
  })
});
var checkoutSchema = z.object({
  event: z.literal("checkout"),
  ecommerce: z.object({
    checkout: z.object({
      actionField: z.object({
        step: checkoutStepSchema
      }),
      products: z.array(productSchema)
    })
  })
});
var checkoutOptionSchema = z.object({
  event: z.literal("checkoutOption"),
  ecommerce: z.object({
    checkout: z.object({
      actionField: z.object({
        step: checkoutStepSchema,
        option: z.union([
          z.literal("Confirmed"),
          z.literal("Accepted"),
          z.literal("Failed")
        ]).optional()
      })
    })
  })
});
var purchaseSchema = z.object({
  event: z.literal("purchase"),
  discountScheme: z.boolean(),
  ecommerce: z.object({
    purchase: z.object({
      actionField: z.object({
        id: z.string(),
        currencyCode: z.union([
          z.literal("GBP"),
          z.literal("EUR"),
          z.literal("DKK")
        ]),
        affiliation: z.literal("PAXOB"),
        revenue: z.string(),
        tax: z.literal("0.00"),
        shipping: z.literal("0.00"),
        payment_method: z.string(),
        coupon: z.string()
      }),
      products: z.array(productSchema)
    })
  })
});
var singleQuoteGoodSchema = z.object({
  cargoType: z.string().optional(),
  cubicMeters: z.number().optional(),
  description: z.string().optional(),
  grossWeight: z.number().optional(),
  hazardous: z.union([
    z.literal("Hazardous"),
    z.literal("LimitedQuantity"),
    z.literal("NotHazardous")
  ]).optional(),
  height: z.number().optional(),
  length: z.number().optional(),
  loadingMeters: z.number().optional(),
  nettoWeight: z.number().optional(),
  packageType: z.number().optional(),
  quantity: z.number().optional(),
  stack: z.boolean().optional(),
  temperatureControlled: z.boolean().optional(),
  width: z.number().optional()
});
var customerSchema = z.object({
  address: z.string().optional(),
  customClearanceBookingsOnly: z.boolean().optional(),
  eoriNumber: z.number().optional(),
  id: z.number().optional(),
  isBookingReferenceRequired: z.boolean().optional(),
  name: z.string().optional(),
  number: z.number().optional(),
  organizationRegistrationNumber: z.number().optional()
});
var documentSchema = z.object({
  category: z.union([
    z.literal("CATCH"),
    z.literal("CHED"),
    z.literal("COMINV"),
    z.literal("CSTMS"),
    z.literal("E2"),
    z.literal("EMAIL"),
    z.literal("EXPDEC"),
    z.literal("HAZ"),
    z.literal("HCERT"),
    z.literal("IMPDEC"),
    z.literal("INVOICE"),
    z.literal("IPAFFS"),
    z.literal("PACK"),
    z.literal("POD"),
    z.literal("REPORT"),
    z.literal("T1"),
    z.literal("T2"),
    z.literal("TRNSP")
  ]).optional(),
  name: z.string(),
  showPODDownload: z.boolean().optional(),
  url: z.string()
});
var organizationSchema = z.object({
  id: z.number().optional(),
  name: z.string().optional()
});
var borderLocationSchema = z.object({
  city: z.string().optional(),
  countryCode: z.string().optional(),
  countryName: z.string().optional(),
  dateTime: z.string().optional(),
  portCode: z.string().optional(),
  portName: z.string().optional()
});
var equipmentTypeSchema = z.object({
  description: z.string().optional(),
  id: z.number().optional(),
  name: z.string().optional()
});
var serviceCodeDetailsSchema = z.object({
  code: z.string().optional(),
  name: z.string().optional()
});
var bookingDocumentsSchema = z.object({
  hasDocuments: z.boolean().optional(),
  invoices: z.array(documentSchema).optional(),
  pods: z.array(documentSchema).optional(),
  reports: z.array(documentSchema).optional()
});
var bookingAddressSchema = z.object({
  addressType: z.number().optional(),
  childExternalId: z.string().optional(),
  contact: z.string().optional(),
  freightLocation: z.string().optional(),
  id: z.string().optional(),
  reference: z.string().optional(),
  requestedDate: z.string().optional(),
  requestedFromTime: z.string().optional(),
  requestedFromTimeDate: z.string().optional(),
  requestedToTime: z.string().optional(),
  requestedToTimeDate: z.string().optional()
});
var formSchema = z.object({
  event: z.union([
    z.literal("formSubmit"),
    z.literal("formRender"),
    z.literal("formValidationError"),
    z.literal("form_submit"),
    z.literal("formSubmitGeneral"),
    z.literal("form_submit_general"),
    z.literal("form_render"),
    z.literal("form_validation_error")
  ]),
  stage: z.string().optional(),
  customerEmail: z.string().optional(),
  driveForUs: z.string().optional(),
  formID: z.string(),
  formName: z.string(),
  countryCode: z.string(),
  languageCode: z.string(),
  conversionType: z.string().optional(),
  conversionPoint: z.string().optional(),
  status: z.string().optional(),
  errorCorrelationId: z.string().optional(),
  errorElementName: z.string().optional(),
  errorMessageId: z.string().optional(),
  errorMessage: z.string().optional(),
  businessArea: businessAreaSchema.optional(),
  bookingEngine: z.literal("dotcom").optional(),
  gaCookie: z.string().optional(),
  pageId: z.string().optional(),
  companyEmail: z.string().optional(),
  companyLocation: z.string().optional(),
  companyName: z.string().optional(),
  companyPhoneNumber: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  form_payload: z.string().optional()
});
var additionalPayloadSchema = z.object({});
var addToCartSchema = z.object({
  event: z.union([z.literal("addToCart"), z.literal("add_to_cart")]),
  ecommerce: z.object({
    currencyCode: z.union([
      z.literal("GBP"),
      z.literal("EUR"),
      z.literal("DKK")
    ]),
    add: z.object({
      products: z.array(productSchema)
    })
  })
});
var orderCompleteSchema = z.object({
  event: z.literal("Order Completed"),
  vehicle_type: vehicleTypeSchema.optional(),
  vehicle_count: vehicleCountSchema.optional(),
  adult_count: adultCountSchema.optional(),
  children_count: childrenCountSchema.optional(),
  children_age: childrenAgeSchema.optional(),
  infant_count: infantCountSchema.optional(),
  pet_count: petCountSchema.optional(),
  booking_engine: z.union([
    z.literal("dotcom"),
    z.literal("tourOperator"),
    z.literal("futureFreight"),
    z.literal("seabookFlow"),
    z.literal("seabookAmendment"),
    z.literal("directportal"),
    z.literal("myprofile"),
    z.literal("sqp"),
    z.literal("customsclearance"),
    z.literal("ukapi"),
    z.literal("PAXOB")
  ]).optional(),
  business_area: z.union([
    z.literal("passenger"),
    z.literal("logistics"),
    z.literal("freight"),
    z.literal("group")
  ]).optional(),
  locale: localeSchema.optional(),
  page_id: pageIdSchema.optional(),
  flow: flowSchema.optional(),
  stage: z.union([
    z.literal("see"),
    z.literal("think"),
    z.literal("do"),
    z.literal("care")
  ]).optional(),
  additional_payload: additionalPayloadSchema.optional(),
  order_id: listIdSchema.optional(),
  total: z.number().optional(),
  currency: z.string().optional(),
  products: cartIdSchema.optional(),
  cart_id: emailSchema,
  email: gaClientIdSchema.optional(),
  ga_client_id: z.string().optional(),
  loyalty_program_name: z.string().optional(),
  loyalty_program_points: z.number().optional()
});
var subBookingSchema = z.object({
  collectionAddress: bookingAddressSchema.optional(),
  collectionEquipmentItem: z.string().optional(),
  collectionTruck: z.string().optional(),
  deliveryAddress: bookingAddressSchema.optional(),
  deliveryEquipmentItem: z.string().optional(),
  deliveryTruck: z.string().optional(),
  externalReference: z.string().optional(),
  goods: z.string().optional(),
  id: z.number().optional(),
  name: z.string().optional()
});
var mlViewTrackTraceSchema = z.object({
  event: z.literal("mlViewTrackTrace"),
  market: z.literal("INT"),
  currentAssociateName: z.string().optional(),
  currentAssociateVatNumber: z.string().optional(),
  customerId: z.number().optional(),
  customerUserId: z.number().optional(),
  role: z.union([z.literal("quote"), z.literal("agreement")]),
  impersonation: z.boolean(),
  impersonationId: z.string().optional(),
  isPrimaryAssociate: z.boolean(),
  countryCode: z.literal("INT"),
  languageCode: z.string().optional(),
  bookingEngine: z.literal("directportal"),
  businessArea: z.literal("logistics"),
  customerEmail: z.string().optional(),
  flow: z.literal("directonboarding"),
  userID: z.string().optional(),
  cargoType: z.string().optional(),
  collectionCity: z.string().optional(),
  collectionCountry: z.string().optional(),
  collectionCounty: z.string().optional(),
  collectionPostCode: z.string().optional(),
  creationDate: z.string().optional(),
  currency: z.string().optional(),
  deliveryCity: z.string().optional(),
  deliveryCountry: z.string().optional(),
  deliveryCounty: z.string().optional(),
  deliveryPostCode: z.string().optional(),
  equipmentGroup: z.union([
    z.literal("COND"),
    z.literal("CONR"),
    z.literal("SP_FLAT"),
    z.literal("SP_MFL"),
    z.literal("TRA"),
    z.literal("TRAM"),
    z.literal("TRAR")
  ]).optional(),
  expiryDate: z.string().optional(),
  goods: z.array(singleQuoteGoodSchema).optional(),
  hazardous: z.union([
    z.literal("Hazardous"),
    z.literal("LimitedQuantity"),
    z.literal("NotHazardous")
  ]).optional(),
  lastBookingDate: z.string().optional(),
  loadType: z.string(),
  price: z.number().optional(),
  quoteType: z.union([
    z.literal("INSTANT"),
    z.literal("MANUAL"),
    z.literal("SEMIINSTANT")
  ]).optional(),
  requestedByEmail: z.string().optional(),
  serviceLevelCodes: z.array(z.string()).optional(),
  status: z.string().optional(),
  transitTime: z.number().optional(),
  bookingDocuments: bookingDocumentsSchema.optional(),
  collectionCount: z.string().optional(),
  consignmentId: z.number().optional(),
  containerRegNumber: z.string().optional(),
  createdBy: z.string().optional(),
  createdByExternalReferenceId: z.number().optional(),
  customer: customerSchema.optional(),
  customerReference: z.string().optional(),
  customerRemarks: z.string().optional(),
  customsClearanceBooking: z.boolean().optional(),
  customsClearanceServiceApplicable: z.boolean().optional(),
  customsClearanceServiceRequest: z.union([
    z.literal("EXPORT_DFDS"),
    z.literal("IMPORT_DFDS"),
    z.literal("IMPORT_EXPORT_CUSTOMER"),
    z.literal("IMPORT_EXPORT_DFDS"),
    z.literal("NOT_APPLICABLE")
  ]).optional(),
  customsExportEditingAllowed: z.boolean().optional(),
  customsImportEditingAllowed: z.boolean().optional(),
  deliveryCount: z.string().optional(),
  documents: z.array(documentSchema).optional(),
  entryMRN: z.string().optional(),
  exitMRN: z.string().optional(),
  exportDeclarationState: z.union([
    z.literal("ACCEPTED"),
    z.literal("CLEARED"),
    z.literal("PENDING"),
    z.literal("REJECTED")
  ]).optional(),
  exportMRN: z.string().optional(),
  firstCollection: z.string().optional(),
  firstCollectionDate: z.string().optional(),
  hasMissingCustomsDetails: z.boolean().optional(),
  hasPendingQCApproval: z.boolean().optional(),
  id: z.string(),
  importDeclarationState: z.union([
    z.literal("ACCEPTED"),
    z.literal("CLEARED"),
    z.literal("PENDING"),
    z.literal("REJECTED")
  ]).optional(),
  importMRN: z.string().optional(),
  internalRemarks: z.string().optional(),
  isNeutralDelivery: z.boolean().optional(),
  lastDelivery: z.string().optional(),
  lastDeliveryDate: z.string().optional(),
  lastModified: z.string().optional(),
  lastModifiedBy: z.string().optional(),
  lastSynced: z.number().optional(),
  loading: z.boolean().optional(),
  organization: organizationSchema.optional(),
  portArrival: borderLocationSchema.optional(),
  portDeparture: borderLocationSchema.optional(),
  preloadFailed: z.boolean().optional(),
  quoteId: z.number().optional(),
  rejectReason: z.string().optional(),
  requestedEquipmentType: equipmentTypeSchema.optional(),
  scheduleExternalReferenceId: z.number().optional(),
  scheduleId: z.string().optional(),
  scheduleIsActive: z.boolean().optional(),
  scheduleTitle: z.string().optional(),
  serviceLevels: z.array(serviceCodeDetailsSchema).optional(),
  state: z.number(),
  subBookings: z.array(subBookingSchema).optional(),
  tracking: z.string().optional(),
  trailerRegNumber: z.string().optional(),
  transportTerm: z.number().optional(),
  truckRegNumber: z.string().optional(),
  vesselName: z.string().optional(),
  trackingAllowed: z.boolean(),
  mlTrackTraceStatus: z.union([
    z.literal("not_available"),
    z.literal("active"),
    z.literal("not_active"),
    z.literal("no_data_past_delivery_date"),
    z.literal("no_data_past_collection_date")
  ]).optional()
});
var dataContractSchema = z.union([
  authClickNavSchema,
  authClickRequestSchema,
  authErrorSchema,
  authInitSchema,
  authUpdatePasswordSchema,
  authSuccessSchema,
  optimizeActivateSchema,
  buttonClickSchema,
  formValidationErrorSchema,
  discountSchemeSchema,
  clickElementSchema,
  addToCartSchema,
  removeFromCartSchema,
  checkoutSchema,
  checkoutOptionSchema,
  purchaseSchema,
  formSubmitSchema,
  formSubmitErrorSchema,
  formValidationErrorPassengerSchema,
  verifyPassengerDetailsSchema,
  bookingTrackingFutureFreightSchema,
  companyChangeTrackingSchema,
  introTrackingSchema,
  virtualPageTrackingFutureFreightSchema,
  searchTrackingSchema,
  tableSortTrackingSchema,
  trackFileUploadSchema,
  trackFileDownloadSchema,
  trackInvoiceDownloadSchema,
  loginComponentSchema,
  noBookingsFoundSchema,
  buttonClickedSchema,
  originalLocationSchema,
  videoSchema,
  countrySwitchSchema,
  overlayTrackingSchema,
  accordionClickSchema,
  newsletterSubscribeSchema,
  newsletterUnsubscribeSchema,
  browserNotSupportedSchema,
  cookieConsentSchema,
  mlNewAddressSchema,
  audienceExclusionSchema,
  formSubmitAPIFailSchema,
  formSubmitSuccessSchema,
  formSubmitValidationFailSchema,
  userRegisterLoginSchema,
  bookingAccessRequestedSchema,
  clearFormStateSchema,
  clearTemplateSchema,
  companyChangeSchema,
  createTemplateSchema,
  dfdsDirectSearchSchema,
  displayBannerSchema,
  errorSchema,
  errorOccuredSchema,
  formValidationError2Schema,
  formValidationErrorCargoSchema,
  mlAddPackageSchema,
  mlClearSearchSchema,
  mlClickNewBookingSchema,
  mlClickNewClearanceSchema,
  mlClickRefreshBookingsListSchema,
  mlDeleteBookingSchema,
  mlDeletePackageSchema,
  mlDownloadWindowSchema,
  mlinstantRequestSchema,
  mlSortBySchema,
  mlUploadSchema,
  selectTemplateSchema,
  updateTemplateSchema,
  mlBookingSchema,
  mlDuplicateBookingSchema,
  mlEditBookingSchema,
  mlCustomsBookingSchema,
  mlDeleteCustomsSchema,
  mlEditCustomsSchema,
  mlViewCustomsSchema,
  mlDownloadSchema,
  mlDownloadinvoiceSchema,
  mlDownloadPodSchema,
  mlCreateScheduleSchema,
  mlDeactivateScheduleSchema,
  mlEditScheduleSchema,
  mlViewScheduleSchema,
  mlCreateBookingSchema,
  mlDraftBookingSchema,
  mlViewTrackTraceSchema,
  formSchema,
  vpvGeneralSchema,
  vpvCustomerAccountSchema,
  vpvBookingExperienceSchema,
  vpvMfPassengerSchema,
  menuClickSchema,
  productChangeSchema,
  orderCompleteSchema,
  bookingStatusSchema,
  travelSearchInitializeSchema,
  travelSearchResetSchema,
  travelSearchSubmitSchema,
  travelSearchOptionsSchema,
  bookingAbandonedSchema,
  careersVacanciesFilterSchema,
  careersVacancyClickSchema
]);

// src/dispatchers/segment/api.ts
var verifyAnalytics = (track2) => {
  var _a, _b;
  return ((_a = window.analytics) == null ? void 0 : _a.initialized) ? track2() : (_b = window.analytics) == null ? void 0 : _b.ready(track2);
};
var functionDisallowed = (fullConsent) => !(isBrowser_default && fullConsent);
var getGAClientContext = () => ({
  integrations: { "Google Analytics": { clientId: getGaClientId_default() } }
});
var trackEvent = (trackingMethod, _a) => {
  var { event, properties = {}, options: _b = {} } = _a, _c = _b, { context } = _c, rest = __objRest(_c, ["context"]);
  const { consent, fullConsent } = getCookieConsent_default();
  if (functionDisallowed(fullConsent)) return;
  return verifyAnalytics(() => {
    var _a2;
    (_a2 = window.analytics) == null ? void 0 : _a2[trackingMethod](
      event,
      objKeysCaseConverter_default(properties),
      __spreadProps(__spreadValues({}, rest), {
        context: __spreadValues(__spreadValues({}, context), getGAClientContext()),
        consent
      })
    );
  });
};
var segmentPage = async (args) => trackEvent("page", overwriteDefaultPageProps_default(args));
var segmentTrack = async (_a) => {
  var _b = _a, { properties } = _b, rest = __objRest(_b, ["properties"]);
  return trackEvent("track", __spreadValues({
    properties: __spreadValues(__spreadValues({}, getTrackingData()), properties)
  }, rest));
};
var segmentIdentify = async ({
  userId,
  traits,
  options
}) => {
  const { consent, fullConsent } = getCookieConsent_default();
  if (functionDisallowed(fullConsent)) return;
  return verifyAnalytics(
    () => {
      var _a;
      return (_a = window.analytics) == null ? void 0 : _a.identify(
        userId || null,
        objKeysCaseConverter_default(traits),
        __spreadProps(__spreadValues({}, options), {
          context: getGAClientContext(),
          consent
        })
      );
    }
  );
};

// src/dispatchers/segment/events/authSuccess.ts
var authSuccess = (eventPayload) => {
  var _a, _b;
  (_a = segmentTrack) == null ? void 0 : _a({
    event: "auth_success",
    properties: eventPayload
  });
  if (eventPayload.user_email) {
    const traits = {
      email: eventPayload.user_email,
      first_name: eventPayload.user_first_name
    };
    (_b = segmentIdentify) == null ? void 0 : _b({
      userId: eventPayload.user_id,
      traits
    });
  }
};
var authSuccess_default = authSuccess;

// src/dispatchers/segment/events/careersVacanciesFilter.ts
var vacanciesAppFilter = ({
  area,
  location,
  vacancies
}) => {
  var _a;
  (_a = segmentTrack) == null ? void 0 : _a({
    event: "Career filter applied",
    properties: { area, location, vacancies }
  });
};
var careersVacanciesFilter_default = vacanciesAppFilter;

// src/dispatchers/segment/events/careersVacancyClick.ts
var vacanciesAppClick = ({
  vacancyId,
  area,
  location,
  vacancyTitle,
  vacancyUrl,
  vacancyPostingDate
}) => {
  var _a;
  (_a = segmentTrack) == null ? void 0 : _a({
    event: "Vacancy clicked",
    properties: {
      vacancyId,
      area,
      location,
      vacancyTitle,
      vacancyUrl,
      vacancyPostingDate
    }
  });
};
var careersVacancyClick_default = vacanciesAppClick;

// src/dispatchers/segment/events/form.ts
var eventNameMap = {
  formSubmit: "Form Submitted",
  formRender: "Form Rendered",
  formValidationError: "Form Validation Failed"
  // formSubmissionFailed: 'Form Submission Failed', TODO: uncomment when they are defined in DataContract.json
  // formConversion: 'Form Conversion', TODO: uncomment when they are defined in DataContract.json
};
var form = (_a) => {
  var _b = _a, {
    status,
    formID,
    event,
    customerEmail,
    companyEmail
  } = _b, rest = __objRest(_b, [
    "status",
    "formID",
    "event",
    "customerEmail",
    "companyEmail"
  ]);
  var _a2, _b2;
  const email = customerEmail || companyEmail;
  (_a2 = segmentTrack) == null ? void 0 : _a2({
    event: eventNameMap[event],
    properties: __spreadValues({
      status,
      formId: formID,
      email
    }, rest)
  });
  if (email) {
    const traits = {
      email,
      phone: rest.companyPhoneNumber,
      company: rest.companyName,
      firstName: rest.firstName,
      lastName: rest.lastName
    };
    (_b2 = segmentIdentify) == null ? void 0 : _b2({
      traits,
      userId: getUserSegmentId_default().dfdsUserId
    });
  }
};
var form_default = form;

// src/dispatchers/segment/events/vpvGeneral.ts
var eventToSegmentEventMap = {
  ["dotcom" /* DOTCOM */]: [
    "b2b" /* B2B */,
    "logistics" /* LOGISTICS */,
    "passenger" /* PASSENGER */,
    "group" /* GROUP */,
    "freight_ferries_and_logistics" /* FREIGHT_AND_LOGISTICS */,
    "about" /* ABOUT */
  ],
  ["directportal" /* DIRECT_PORTAL */]: ["logistics" /* LOGISTICS */],
  ["PAXOB" /* PAXOB */]: ["passenger" /* PASSENGER */]
};
var vpvGeneral = (_a) => {
  var _b = _a, {
    statusCode,
    contentfulId,
    contentfulMarker,
    event
  } = _b, rest = __objRest(_b, [
    "statusCode",
    "contentfulId",
    "contentfulMarker",
    "event"
  ]);
  var _a2, _b2;
  const { bookingEngine, businessArea } = rest;
  if ((_a2 = eventToSegmentEventMap[bookingEngine == null ? void 0 : bookingEngine.toLowerCase()]) == null ? void 0 : _a2.includes(
    businessArea == null ? void 0 : businessArea.toLowerCase()
  ))
    (_b2 = segmentPage) == null ? void 0 : _b2({
      event: "Page Viewed",
      properties: __spreadProps(__spreadValues({}, rest), {
        statusCode: parseInt(statusCode),
        locale: contentfulMarker,
        pageId: contentfulId
      })
    });
};
var vpvGeneral_default = vpvGeneral;

// src/dispatchers/segment/events/localeSelectorClick.ts
var localeSelectorClick = ({
  eventAction,
  clickedElementText
}) => {
  var _a;
  (_a = segmentTrack) == null ? void 0 : _a({
    event: "Locale selector button clicked",
    properties: {
      eventAction,
      clickedElementText
    }
  });
};
var localeSelectorClick_default = localeSelectorClick;

// src/dispatchers/segment/events/localeSwitchClick.ts
var localeSwitchClick = ({
  currentLocale,
  newLocale,
  languageOption
}) => {
  var _a;
  (_a = segmentTrack) == null ? void 0 : _a({
    event: "Locale switch button clicked",
    properties: {
      currentLocale,
      newLocale,
      languageOption
    }
  });
};
var localeSwitchClick_default = localeSwitchClick;

// src/dispatchers/segment/segmentPush.ts
var eventHandlers = [
  {
    type: getEventLiterals_default(authSuccessSchema),
    schema: authSuccessSchema,
    eventDispatcher: authSuccess_default
  },
  {
    type: getEventLiterals_default(formSchema),
    schema: formSchema,
    eventDispatcher: form_default
  },
  {
    type: getEventLiterals_default(vpvGeneralSchema),
    schema: vpvGeneralSchema,
    eventDispatcher: vpvGeneral_default
  },
  {
    type: getEventLiterals_default(careersVacanciesFilterSchema),
    schema: careersVacanciesFilterSchema,
    eventDispatcher: careersVacanciesFilter_default
  },
  {
    type: getEventLiterals_default(careersVacancyClickSchema),
    schema: careersVacancyClickSchema,
    eventDispatcher: careersVacancyClick_default
  },
  {
    type: getEventLiterals_default(buttonClickSchema),
    schema: buttonClickSchema,
    eventDispatcher: localeSelectorClick_default
  },
  {
    type: getEventLiterals_default(countrySwitchSchema),
    schema: countrySwitchSchema,
    eventDispatcher: localeSwitchClick_default
  }
];
var segmentPush = async (eventPayload) => {
  var _a;
  if (!((_a = getCookieConsent_default()) == null ? void 0 : _a.fullConsent)) return;
  for (const { type, schema, eventDispatcher } of eventHandlers) {
    if (Object.values(type).includes(eventPayload.event)) {
      const event = schema.safeParse(eventPayload);
      if (event.success) {
        eventDispatcher(event.data);
        break;
      } else {
        const errorMsg = `${eventPayload.event} dispatcher: validation failed: ${JSON.stringify(event.error)}`;
        console.error(errorMsg);
      }
    }
  }
};
var segmentPush_default = segmentPush;

// src/middleware/nonMutating/blocking/setStorage.ts
var setStorage = async (eventPayload) => {
  const vpv = vpvGeneralSchema.safeParse(eventPayload);
  if (vpv.success) {
    setTrackingData({
      businessArea: vpv.data.businessArea,
      bookingEngine: vpv.data.bookingEngine,
      countryCode: vpv.data.countryCode,
      languageCode: vpv.data.languageCode,
      pageId: vpv.data.contentfulId
    });
  }
};
var setStorage_default = setStorage;
var runtimeGlobalEventValidator = async (eventPayload) => {
  const dataContract = dataContractSchema.safeParse(eventPayload);
  if (!dataContract.success) {
    const issueOnGlobalValidation = dataContract.error.issues[0];
    let errorMsg = dataContract.error.message;
    if ((issueOnGlobalValidation == null ? void 0 : issueOnGlobalValidation.code) === "invalid_union") {
      errorMsg = `Tracking payload validation error for the event "${eventPayload.event}". Please ensure that a payload complies with the type definitions for this event`;
    }
    captureException(new Error(errorMsg, { cause: dataContract.error }));
  }
};
var runtimeGlobalEventValidator_default = runtimeGlobalEventValidator;

// src/trackClient/track.ts
var dispatch = async (eventPayload) => await Promise.allSettled([
  segmentPush_default(eventPayload),
  dataLayerPush_default(eventPayload)
]);
var nonMutatingMiddlewares = async (eventPayload) => {
  const nonBlocking = [runtimeGlobalEventValidator_default(eventPayload)];
  const blocking = [setStorage_default(eventPayload)];
  return Promise.allSettled([...nonBlocking, ...blocking]);
};
async function track(_eventPayload) {
  const eventPayload = _eventPayload;
  try {
    assertEventPayload_default(eventPayload);
    nonMutatingMiddlewares(eventPayload);
    assertEventPayload_default(eventPayload);
    const results = await dispatch(eventPayload);
    const dispatchErrors = results.flatMap(
      (result) => result.status === "rejected" ? result.reason : []
    );
    if (dispatchErrors.length) throw dispatchErrors;
  } catch (e) {
    if (e instanceof Array) {
      const errors = e.flatMap(
        (error) => error instanceof DispatchError_default || error instanceof NonMutatingMiddlewareError_default || error instanceof MutatingMiddlewareError_default ? error.message : [error]
      ).join("\n\n");
      logErrorFormatter_default(errors, _eventPayload);
      return;
    }
    if (e instanceof EventPayloadError) {
      logErrorFormatter_default(`${e.name}: ${e.message}`, _eventPayload);
      return;
    }
    logErrorFormatter_default("unhandled error", e);
  }
}
var track_default = track;

// src/trackServer/tracking-methods/types.ts
var EventName = /* @__PURE__ */ ((EventName2) => {
  EventName2["QUOTE_REQUESTED"] = "Quote Requested";
  EventName2["QUOTE_CREATED"] = "Quote Created";
  EventName2["QUOTE_UPDATED"] = "Quote Updated";
  EventName2["BOOKING_CREATED"] = "Booking Created";
  EventName2["FORM_SUBMITTED"] = "Form Submitted";
  EventName2["FORM_SUBMISSION_FAILED"] = "Form Submission Failed";
  EventName2["PRODUCT_ADDED"] = "Product Added";
  EventName2["PRODUCT_REMOVED"] = "Product Removed";
  EventName2["ORDER_COMPLETED"] = "Order Completed";
  return EventName2;
})(EventName || {});

// src/trackServer/tracking-methods/events.ts
var events = (client, config) => {
  const sendEvent = (message) => {
    var _a, _b;
    return ((_b = (_a = message == null ? void 0 : message.context) == null ? void 0 : _a.consent) == null ? void 0 : _b.all_cookies_accepted) && trackAnalytics_default(message, client, config);
  };
  return {
    quoteRequested: (props) => sendEvent(__spreadValues({ event: "Quote Requested" /* QUOTE_REQUESTED */ }, props)),
    quoteCreated: (props) => sendEvent(__spreadValues({ event: "Quote Created" /* QUOTE_CREATED */ }, props)),
    quoteUpdated: (props) => sendEvent(__spreadValues({ event: "Quote Updated" /* QUOTE_UPDATED */ }, props)),
    bookingCreated: (props) => sendEvent(__spreadValues({ event: "Booking Created" /* BOOKING_CREATED */ }, props)),
    formSubmitted: (props) => {
      const _a = props, { properties } = _a, message = __objRest(_a, ["properties"]);
      const enrichedProperties = __spreadProps(__spreadValues({}, properties), {
        status: "success"
      });
      sendEvent(__spreadProps(__spreadValues({
        event: "Form Submitted" /* FORM_SUBMITTED */
      }, message), {
        properties: __spreadValues({}, enrichedProperties)
      }));
    },
    formSubmissionFailed: (props) => {
      const _a = props, { properties } = _a, message = __objRest(_a, ["properties"]);
      const enrichedProperties = __spreadProps(__spreadValues({}, properties), {
        status: "fail",
        error_type: "APIError"
      });
      sendEvent(__spreadProps(__spreadValues({
        event: "Form Submission Failed" /* FORM_SUBMISSION_FAILED */
      }, message), {
        properties: __spreadValues({}, enrichedProperties)
      }));
    },
    productAdded: (props) => sendEvent(__spreadValues({ event: "Product Added" /* PRODUCT_ADDED */ }, props)),
    productRemoved: (props) => sendEvent(__spreadValues({ event: "Product Removed" /* PRODUCT_REMOVED */ }, props)),
    orderCompleted: (props) => sendEvent(__spreadValues({ event: "Order Completed" /* ORDER_COMPLETED */ }, props))
  };
};
var events_default = events;

// src/trackServer/tracking-methods/group.ts
var group = (message, client) => {
  const _a = message, { user_id, anonymous_id, group_id, traits } = _a, messageContext = __objRest(_a, ["user_id", "anonymous_id", "group_id", "traits"]);
  const _b = traits || {}, { additional_payload } = _b, groupTraits = __objRest(_b, ["additional_payload"]);
  validateUserId_default(message);
  const data = __spreadValues({
    userId: user_id || "",
    anonymousId: anonymous_id || "",
    groupId: group_id.toString(),
    traits: __spreadValues(__spreadValues({}, groupTraits), additional_payload)
  }, messageContext);
  if (!message.context.consent.all_cookies_accepted) return false;
  client.group(data);
};
var group_default = group;

// src/trackServer/tracking-methods/identify.ts
var identify = (message, client) => {
  const _a = message, { user_id, anonymous_id, traits, context } = _a, messageContext = __objRest(_a, ["user_id", "anonymous_id", "traits", "context"]);
  validateUserId_default(message);
  const _b = traits || {}, { ga_client_id, additional_payload } = _b, userTraits = __objRest(_b, ["ga_client_id", "additional_payload"]);
  const _context = __spreadValues(__spreadValues({}, ga_client_id && {
    integrations: { "Google Analytics": { clientId: ga_client_id } }
  }), context);
  const data = __spreadValues({
    userId: user_id || "",
    anonymousId: anonymous_id || "",
    traits: __spreadValues(__spreadValues({}, userTraits), additional_payload),
    context: __spreadValues({}, _context)
  }, messageContext);
  if (!message.context.consent.all_cookies_accepted) return false;
  client.identify(data);
};
var identify_default = identify;

// src/trackServer/tracking-methods/createEventSender.ts
var createEventSender = (client, config) => {
  return {
    identifyUser: (message) => identify_default(message, client),
    groupUser: (message) => group_default(message, client),
    trackEvent: events_default(client, config)
  };
};
var createEventSender_default = createEventSender;

// src/trackServer/tracking-methods/trackAnalytics.ts
var trackAnalytics = (message, client, config) => {
  const _a = message, {
    event,
    user_id,
    anonymous_id = "",
    properties,
    context
  } = _a, messageContext = __objRest(_a, [
    "event",
    "user_id",
    "anonymous_id",
    "properties",
    "context"
  ]);
  validateUserId_default(message);
  const _b = properties || {}, {
    locale = "",
    ga_client_id,
    additional_payload
  } = _b, rest = __objRest(_b, [
    "locale",
    "ga_client_id",
    "additional_payload"
  ]);
  const { country_code, language_code } = splitLocaleSnakeCase_default(locale);
  const anonymousId = config.enableAnonymousIdAutoGeneration ? crypto.randomUUID() : anonymous_id;
  const _context = __spreadValues(__spreadValues({}, ga_client_id && {
    integrations: { "Google Analytics": { clientId: ga_client_id } }
  }), context);
  const data = __spreadValues({
    event,
    anonymousId,
    userId: user_id,
    properties: __spreadValues(__spreadValues({
      locale,
      country_code,
      language_code
    }, additional_payload), rest),
    context: __spreadValues({}, _context)
  }, messageContext);
  if (!message.context.consent.all_cookies_accepted || config.disableAnonymousTraffic && !user_id)
    return false;
  client.track(data);
};
var trackAnalytics_default = trackAnalytics;

// src/trackServer/config/client.ts
var WRITE_KEY_ERROR = "You must pass your Segment projects write key.";
var defaultConfig = {
  writeKey: "",
  flushAt: 1,
  flushInterval: 1e3,
  disableAnonymousTraffic: true,
  enableAnonymousIdAutoGeneration: false
};
var segmentClient = (userConfig) => {
  const config = __spreadValues(__spreadValues({}, defaultConfig), userConfig);
  const _a = config, { writeKey, flushAt, flushInterval } = _a, rest = __objRest(_a, ["writeKey", "flushAt", "flushInterval"]);
  if (!writeKey) throw WRITE_KEY_ERROR;
  const client = new Analytics(__spreadValues({
    writeKey,
    flushAt,
    flushInterval
  }, rest));
  const { identifyUser, trackEvent: trackEvent2, groupUser } = createEventSender_default(
    client,
    config
  );
  return { identifyUser, trackEvent: trackEvent2, groupUser };
};
var client_default = segmentClient;

export { EventName, getCookie_default as getCookie, getFromLocalStorage_default as getFromLocalStorage, getFromSessionStorage_default as getFromSessionStorage, getGaClientId_default as getGaClientId, getTrackingData, getUserSegmentId_default as getUserSegmentId, isBrowser_default as isBrowser, removeFromLocalStorage_default as removeFromLocalStorage, removeFromSessionStorage_default as removeFromSessionStorage, client_default as segmentClient, setCookie_default as setCookie, setToLocalStorage_default as setToLocalStorage, setToSessionStorage_default as setToSessionStorage, setTrackingData, splitLocale_default as splitLocale, track_default as track };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map